'use client';

import React, { useCallback } from 'react';
import { Alert } from 'reactstrap';
import { isEmpty } from 'lodash';
import moment from 'moment-timezone';
import { generateRandomString } from 'src/helpers/utils';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hooks';
import { updateVisibility } from 'src/redux/modals/slice';
import type { ActivityLogEntry } from 'src/types';

// Status emitted by the backend when the provider closes the job, before Lula QC.
// This entry surfaces the vendor's notes/media to the customer immediately (pre-QC).
const PROVIDER_CLOSED_STATUS = 'provider_closed';

// Statuses whose entries render Job Description + Service Note + Before/After photos.
const COMPLETION_STATUSES = [
  'work_completed',
  'closed',
  'initial_work_completed',
  PROVIDER_CLOSED_STATUS,
];

// Status -> Message mapping
const statusMessages: Record<string, string> = {
  created: 'Job status changed to Triage / Requesting Availability',
  scheduling_in_progress: 'Job Scheduling in Progress',
  started: 'Job Started',
  'enroute-stopped': 'Pro stopped en-route',
  reached: 'Pro reached job site',
  enrouted: 'Pro en-route',
  [PROVIDER_CLOSED_STATUS]: 'Provider submitted job details',
  quality_check: 'Job undergoing QA by Lula',
  paused: 'Job Paused',
  canceled: 'Job Canceled',
  initial_work_completed: 'Initial Work Completed',
  closed: 'Job Completed',
  work_completed: 'Job Completed',
};

const requestUserOption: Record<string, string> = {
  customer: 'Property Manager',
  resident: 'Resident',
  provider: 'Pro',
};

interface MediaItem {
  type?: string;
  media_type?: string;
  thumbnail_url?: string;
}

interface JobActivityLogCardProps {
  logDetails: ActivityLogEntry;
}

const JobActivityLogCard: React.FC<JobActivityLogCardProps> = ({ logDetails }) => {
  const dispatch = useAppDispatch();
  const timeZone = useAppSelector(
    (state) => state?.jobDetails?.data?.timezone,
  ) as string | undefined;

  const eventAttrs = logDetails?.event_attributes;
  const status = eventAttrs?.to?.value;
  const reScheduleRequestedBy = eventAttrs?.reschedule_requested_by;

  const beforePhotos = Array.isArray(eventAttrs?.media)
    ? eventAttrs?.media?.filter(
      (item) => item?.type
        === 'provider-before-job-media',
    )
    : [];

  const afterPhotos = Array.isArray(eventAttrs?.media)
    ? eventAttrs?.media?.filter((item) => item?.type === 'provider-job-media')
    : [];

  // Helper: open modal with image set
  const handleImageClick = (collection: MediaItem[], index: number, imageType: string) => {
    if (isEmpty(collection)) return;
    dispatch(
      updateVisibility({
        modalType: 'imageSlidesModal',
        isVisible: true,
        extraProps: {
          modalData: {
            collection,
            position: index,
            showDeleteButton: false,
            imageType,
            timeZone,
          },
        },
      }),
    );
  };

  // Helper: render photo preview section
  const renderPhotos = (label: string, photos: MediaItem[], imageType: string) => {
    if (isEmpty(photos)) return null;

    return (
      <div className="preview-image-outer">
        <p>
          {label}
          :
        </p>
        <div className="preview-image-inner">
          {photos.slice(0, 4).map((picture, index) => (
            <div
              key={generateRandomString()}
              className={`preview-image ${picture?.media_type === 'video' ? 'video' : ''}`}
            >
              <img
                src={picture?.thumbnail_url}
                alt=""
                onClick={() => handleImageClick(photos, index, imageType)}
              />
            </div>
          ))}
          {photos.length > 4 && (
            <div
              className="preview-image"
              onClick={() => handleImageClick(photos, 0, imageType)}
            >
              +
              {photos.length - 4}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Helper: format date ranges
  const formatDateRange = useCallback(
    (
      start: string | undefined,
      end: string | undefined,
      tz: string | undefined,
      prefix: string,
    ) => {
      if (!start || !end || !tz) return null;

      const startTime = moment?.utc(start)?.tz(tz);
      const endTime = moment?.utc(end)?.tz(tz);

      const formattedTime = `${startTime?.format('MMM D YYYY')}, ${startTime?.format(
        'h:mm a',
      )} - ${endTime?.format('h:mm a')} ${startTime?.format('z')}`;

      return (
        <React.Fragment>
          {`${prefix}`}
          <span className="date">
            {formattedTime}
          </span>
        </React.Fragment>
      );
    },
    [],
  );

  const serviceCategory = logDetails?.service_category;

  const additionalDetails = eventAttrs?.reason &&  typeof eventAttrs?.reason === 'object' ? eventAttrs.reason.additional_details : '';

  const returnDate = formatDateRange(
    eventAttrs?.return_start_date,
    eventAttrs?.return_end_date,
    timeZone,
    'Return date: ',
  );

  const estimatedFinish = formatDateRange(
    eventAttrs?.job_finish_start_date,
    eventAttrs?.job_finish_end_date,
    timeZone,
    'Estimated Finish Date: ',
  );

  const scheduledDate = formatDateRange(
    eventAttrs?.scheduled_start_time,
    eventAttrs?.scheduled_end_time,
    timeZone,
    `${serviceCategory?.label} job ${reScheduleRequestedBy ? 're-' : ''}scheduled for `,
  );

  return (
    <React.Fragment>

      {/* Generic Status Messages */}
      {status && statusMessages?.[status] && (
        <p className="mb-0">
          <span>{statusMessages[status]}</span>
        </p>
      )}

      {/* Completed / Initial Work Completed / Provider Submitted Section */}
      {status && COMPLETION_STATUSES.includes(status) && (
        <React.Fragment>
          {/* Pre-QC warning: vendor notes/media are shown before Lula QC completes */}
          {status === PROVIDER_CLOSED_STATUS && (
            <Alert className="qa-pending-banner">
              <svg width="16" height="16" viewBox="0 0 14.3333 14.3333" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path
                  d="M7.16667 4.5V7.16667M7.16667 9.83333H7.17333M13.8333 7.16667C13.8333 10.8486 10.8486 13.8333 7.16667 13.8333C3.48477 13.8333 0.5 10.8486 0.5 7.16667C0.5 3.48477 3.48477 0.5 7.16667 0.5C10.8486 0.5 13.8333 3.48477 13.8333 7.16667Z"
                  stroke="#D25100"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              The provider notes and media are being reviewed by Lula&apos;s QA process and may change
            </Alert>
          )}
          {!!eventAttrs?.description && (
            <p>
              <span className="fw-bold d-block">Job Description:</span>
              {' '}
              {eventAttrs?.description}
            </p>
          )}
          {!!eventAttrs?.service_note && (
            <p>
              <span className="fw-bold d-block">
                {status === 'initial_work_completed' || status === PROVIDER_CLOSED_STATUS
                  ? 'Service Note:'
                  : 'Completed Note:'}
              </span>
              {eventAttrs?.service_note}
            </p>
          )}

          <div className="preview-image-wrapper">
            {renderPhotos('Before Media', beforePhotos, 'before-photos')}
            {renderPhotos('After Media', afterPhotos, 'after-photos')}
          </div>
        </React.Fragment>
      )}

      {/* Extra Attributes */}
      {!scheduledDate && !!eventAttrs?.reason && (
        <p className="mb-0">
          Reason:
          {' '}
          {typeof eventAttrs?.reason === 'string' ? eventAttrs?.reason : eventAttrs?.reason?.label}
        </p>
      )}

      {!!additionalDetails && (
        <p className="mb-0">
          Additional Details:
          {' '}
          {additionalDetails}
        </p>
      )}

      {!!eventAttrs?.notes && (
        <p className="mb-0">
          Notes:
          {eventAttrs?.notes}
        </p>
      )}

      {!!eventAttrs?.cancellation_reason && (
        <p className="mb-0">
          Reason:
          {' '}
          {eventAttrs?.cancellation_reason}
        </p>
      )}

      {returnDate && <p className="mb-0">{returnDate}</p>}
      {estimatedFinish && <p className="mb-0">{estimatedFinish}</p>}

      {scheduledDate && (
      <p className="mb-0">
        {scheduledDate}
          {reScheduleRequestedBy
            ? (
              <ul>
                <li>
                  <span>Who requested the change? </span>
                  {requestUserOption?.[reScheduleRequestedBy]}
                </li>
                {eventAttrs?.reason
                  ? (
                    <li>
                      <span>Reason: </span>
                      {typeof eventAttrs?.reason === 'string' ? eventAttrs?.reason : eventAttrs?.reason?.label}
                    </li>
                  )
                  : ''}
              </ul>
            ) : ''}
      </p>
      )}
    </React.Fragment>
  );
};

export default JobActivityLogCard;
